import {
  BaseQueryArg,
  BaseQueryError,
  BaseQueryFn,
  createApi,
  retry,
} from "@reduxjs/toolkit/query/react";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import * as Sentry from "@sentry/react";

const API_URL = import.meta.env.VITE_API_URL || "";

const cmaApiAxiosInstance = axios.create();

cmaApiAxiosInstance.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    Sentry.captureException(error);
    return Promise.reject(error);
  },
);

cmaApiAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    Sentry.captureException(error);
    return Promise.reject(error);
  },
);

type ApiResponse = {
  data?: unknown;
  status: AxiosResponse["status"];
  headers: AxiosResponse["headers"];
};

class FetchError extends Error {
  status: number | string;
  data: any;

  constructor(status: number | string, data: any) {
    super(`Fetch error with status: ${status}`);
    this.status = status;
    this.data = data;
  }
}

const controllerManager = {
  controller: new AbortController(),
};

async function axiosRequest({
  url = "",
  method,
  data,
  needsAuth = true,
  ...config
}: AxiosRequestConfig & { needsAuth?: boolean }): Promise<ApiResponse> {
  let accessToken: string | undefined;

  accessToken = await fetchAuthSession()
    .then(({ tokens }) => tokens?.accessToken.toString())
    .catch(() => undefined);

  if (needsAuth && !accessToken) {
    window.location.href = "/";
  }

  const axiosConfig: AxiosRequestConfig = {
    ...config,
    signal: controllerManager.controller.signal,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(config.headers || {}),
    },
  };

  let axiosResult: AxiosResponse;

  try {
    switch (method) {
      case "GET":
        axiosResult = await cmaApiAxiosInstance.get(url, axiosConfig);
        break;
      case "PUT":
        axiosResult = await cmaApiAxiosInstance.put(url, data, axiosConfig);
        break;
      case "POST":
        axiosResult = await cmaApiAxiosInstance.post(url, data, axiosConfig);
        break;
      case "DELETE":
        axiosResult = await cmaApiAxiosInstance.delete(url, axiosConfig);
        break;
      case "PATCH":
        axiosResult = await cmaApiAxiosInstance.patch(url, data, axiosConfig);
        break;
      case "HEAD":
        axiosResult = await cmaApiAxiosInstance.head(url, axiosConfig);
        break;
      case "OPTIONS":
        axiosResult = await cmaApiAxiosInstance.options(url, axiosConfig);
        break;
      default:
        throw new Error(`Unsupported method: ${method}`);
    }

    return {
      data: axiosResult.data,
      status: axiosResult.status,
      headers: axiosResult.headers,
    };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      throw {
        status: err.response?.status || "FETCH_ERROR",
        data: err.response?.data || err.message,
      };
    }
    throw {
      status: "FETCH_ERROR",
      data: err instanceof Error ? err.message : "Unknown error",
    };
  }
}

const baseQueryConfig =
  ({
    baseUrl,
  }: {
    baseUrl: string;
  }): BaseQueryFn<{
    url: string;
    method: AxiosRequestConfig["method"];
    data?: AxiosRequestConfig["data"];
    params?: AxiosRequestConfig["params"];
    headers?: AxiosRequestConfig["headers"];
    needsAuth?: boolean;
  } | null> =>
  async (baseQueryParams) => {
    if (baseQueryParams === null) return { data: null };

    const { url, method, data, params, headers, needsAuth } = baseQueryParams;
    const config = {
      url: baseUrl + url,
      method,
      data,
      params,
      headers,
      needsAuth,
    };

    try {
      return await axiosRequest(config);
    } catch (error) {
      return { error };
    }
  };

const baseQuery = baseQueryConfig({ baseUrl: API_URL });

const baseQueryRetry = retry(baseQuery, {
  // maxRetries: 3,
  retryCondition: (
    error: BaseQueryError<BaseQueryFn>,
    args: BaseQueryArg<BaseQueryFn>,
    extraArgs: {
      attempt: number;
    },
  ) => {
    if (extraArgs.attempt >= 3) return false;

    if (error instanceof FetchError) {
      if (typeof error.status === "number")
        return error.status >= 500 && error.status < 600;
      if (error.status === "FETCH_ERROR") return true;
    }

    return false;
  },
});

export enum TagTypes {
  Listings = "Listings",
}

export const cmaBackendApi = createApi({
  reducerPath: "cmaBackendApi",
  baseQuery: baseQueryRetry,
  endpoints: (builder) => ({}),
  tagTypes: Object.values(TagTypes),
});
